import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Table, Badge, Row, Col, Alert, Spinner, Nav } from "react-bootstrap";
import {
  fetchDtdcConfigs,
  createDtdcConfig,
  updateDtdcConfig,
  deleteDtdcConfig,
  setDefaultDtdcConfig,
} from "../../actions/product";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { ROLES } from "../../utils/constants";

const DEFAULT_BASE_URL = "https://dtdcapi.shipsy.io/api/customer/integration/consignment";
const DEFAULT_TRACKING_URL = "https://blktracksvc.dtdc.com/dtdc-api/rest/JSONCnTrk/getTrackDetails";

const DEFAULT_ORIGIN_DETAILS = {
  name: "Thathva Industries",
  phone: "9010038559",
  alternate_phone: "9010038559",
  address_line_1: "Bollaramm Village Road",
  address_line_2: "Building No./Flat No.: 14-300/36/A/1",
  pincode: "502300",
  city: "Hyderabad",
  state: "Telangana",
};

const DEFAULT_RETURN_DETAILS = {
  address_line_1: "Building No./Flat No.: 14-300/36/A/1",
  address_line_2: "Bollaramm Village Road",
  city_name: "Hyderabad",
  name: "Thathva Industries",
  phone: "9010038559",
  pincode: "502300",
  state_name: "Telangana",
  email: "support@thathvauniforms.com",
  alternate_phone: "9010038559",
};

const DtdcConfigManager = ({ show, onHide, onConfigChange }) => {
  const { user } = useSelector((state) => state.auth);

  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [showSecrets, setShowSecrets] = useState({});
  const [activeFormTab, setActiveFormTab] = useState("credentials");

  const [formData, setFormData] = useState({
    accountName: "",
    customerCode: "",
    apiKey: "",
    trackingToken: "",
    dtdcBaseUrl: DEFAULT_BASE_URL,
    dtdcTrackingUrl: DEFAULT_TRACKING_URL,
    isDefault: false,
    isActive: true,
    originDetails: { ...DEFAULT_ORIGIN_DETAILS },
    returnDetails: { ...DEFAULT_RETURN_DETAILS },
  });

  useEffect(() => {
    if (show && user?.role === ROLES.SUPER_ADMIN) {
      loadConfigs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, user?.role]);

  const loadConfigs = async () => {
    setLoading(true);
    try {
      const data = await fetchDtdcConfigs();
      setConfigs(Array.isArray(data) ? data : []);
      if (onConfigChange) onConfigChange(data);
    } catch (error) {
      console.error("Failed to load DTDC configs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAdd = () => {
    setEditingConfig(null);
    setActiveFormTab("credentials");
    setFormData({
      accountName: "",
      customerCode: "",
      apiKey: "",
      trackingToken: "",
      dtdcBaseUrl: DEFAULT_BASE_URL,
      dtdcTrackingUrl: DEFAULT_TRACKING_URL,
      isDefault: configs.length === 0,
      isActive: true,
      originDetails: { ...DEFAULT_ORIGIN_DETAILS },
      returnDetails: { ...DEFAULT_RETURN_DETAILS },
    });
    setShowEditModal(true);
  };

  const handleOpenEdit = (config) => {
    setEditingConfig(config);
    setActiveFormTab("credentials");
    setFormData({
      accountName: config.accountName || "",
      customerCode: config.customerCode || "",
      apiKey: config.apiKey || "",
      trackingToken: config.trackingToken || "",
      dtdcBaseUrl: config.dtdcBaseUrl || DEFAULT_BASE_URL,
      dtdcTrackingUrl: config.dtdcTrackingUrl || DEFAULT_TRACKING_URL,
      isDefault: Boolean(config.isDefault),
      isActive: Boolean(config.isActive),
      originDetails: config.originDetails
        ? { ...DEFAULT_ORIGIN_DETAILS, ...config.originDetails }
        : { ...DEFAULT_ORIGIN_DETAILS },
      returnDetails: config.returnDetails
        ? { ...DEFAULT_RETURN_DETAILS, ...config.returnDetails }
        : { ...DEFAULT_RETURN_DETAILS },
    });
    setShowEditModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.customerCode.trim()) {
      toast.warning("Customer Code is required");
      return;
    }
    if (!formData.apiKey.trim()) {
      toast.warning("API Key is required");
      return;
    }
    if (!formData.trackingToken.trim()) {
      toast.warning("Tracking Token is required");
      return;
    }

    setSaving(true);
    try {
      if (editingConfig) {
        await updateDtdcConfig(editingConfig.id, formData);
      } else {
        await createDtdcConfig(formData);
      }
      setShowEditModal(false);
      await loadConfigs();
    } catch (error) {
      console.error("Save DTDC config error:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, code) => {
    if (!window.confirm(`Are you sure you want to delete DTDC configuration for Customer Code "${code}"?`)) {
      return;
    }
    try {
      await deleteDtdcConfig(id);
      await loadConfigs();
    } catch (error) {
      console.error("Delete DTDC config error:", error);
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await setDefaultDtdcConfig(id);
      await loadConfigs();
    } catch (error) {
      console.error("Set default DTDC config error:", error);
    }
  };

  const toggleShowSecret = (id, field) => {
    setShowSecrets((prev) => ({
      ...prev,
      [`${id}_${field}`]: !prev[`${id}_${field}`],
    }));
  };

  const updateOriginField = (field, val) => {
    setFormData((prev) => ({
      ...prev,
      originDetails: { ...prev.originDetails, [field]: val },
    }));
  };

  const updateReturnField = (field, val) => {
    setFormData((prev) => ({
      ...prev,
      returnDetails: { ...prev.returnDetails, [field]: val },
    }));
  };

  if (user?.role !== ROLES.SUPER_ADMIN) {
    return null;
  }

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" centered backdrop="static">
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title className="fs-5 fw-bold">
            <i className="bi bi-truck me-2"></i>DTDC Customer Codes & Account Configurations
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 bg-light">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h6 className="fw-bold mb-1">Configured DTDC Accounts in Database</h6>
              <p className="text-muted small mb-0">
                Manage multiple DTDC Customer Codes, API keys, URLs, and Origin / Return addresses in the database table instead of static .env.
              </p>
            </div>
            <Button variant="success" size="sm" onClick={handleOpenAdd} className="fw-semibold">
              <i className="bi bi-plus-circle me-1"></i> Add Customer Code
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted small">Loading configurations...</p>
            </div>
          ) : configs.length === 0 ? (
            <Alert variant="warning" className="text-center my-4">
              <i className="bi bi-exclamation-triangle-fill fs-4 d-block mb-2"></i>
              <strong>No DTDC configurations found in database table!</strong>
              <p className="small mb-2 mt-1">
                The system is currently using fallback credentials from your <code>.env</code> file.
              </p>
              <Button variant="primary" size="sm" onClick={handleOpenAdd}>
                <i className="bi bi-plus-lg me-1"></i> Add First DTDC Customer Code
              </Button>
            </Alert>
          ) : (
            <div className="table-responsive bg-white rounded shadow-sm border">
              <Table hover align="middle" className="mb-0">
                <thead className="table-light small">
                  <tr>
                    <th>Account Name</th>
                    <th>Customer Code</th>
                    <th>API Key</th>
                    <th>Tracking Token</th>
                    <th>Pickup / Origin Name</th>
                    <th className="text-center">Status</th>
                    <th className="text-center">Default</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody className="small">
                  {configs.map((cfg) => {
                    const showKey = showSecrets[`${cfg.id}_key`];
                    const showToken = showSecrets[`${cfg.id}_token`];
                    const origin = cfg.originDetails || DEFAULT_ORIGIN_DETAILS;

                    return (
                      <tr key={cfg.id}>
                        <td className="fw-bold text-dark">
                          {cfg.accountName || `Account #${cfg.id}`}
                        </td>
                        <td>
                          <Badge bg="dark" className="font-monospace fs-7 px-2 py-1">
                            {cfg.customerCode}
                          </Badge>
                        </td>
                        <td>
                          <div className="d-flex align-items-center font-monospace">
                            <span style={{ maxWidth: "120px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {showKey ? cfg.apiKey : "••••••••••••"}
                            </span>
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 ms-1 text-secondary"
                              onClick={() => toggleShowSecret(cfg.id, "key")}
                              title={showKey ? "Hide" : "Show"}
                            >
                              <i className={`bi bi-eye${showKey ? "-slash" : ""}`}></i>
                            </button>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center font-monospace">
                            <span style={{ maxWidth: "120px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {showToken ? cfg.trackingToken : "••••••••••••"}
                            </span>
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 ms-1 text-secondary"
                              onClick={() => toggleShowSecret(cfg.id, "token")}
                              title={showToken ? "Hide" : "Show"}
                            >
                              <i className={`bi bi-eye${showToken ? "-slash" : ""}`}></i>
                            </button>
                          </div>
                        </td>
                        <td>
                          <div className="fw-semibold text-truncate" style={{ maxWidth: "150px" }} title={origin.name}>
                            {origin.name || "Thathva Industries"}
                          </div>
                          <small className="text-muted">{origin.city || "Hyderabad"}, {origin.pincode || "502300"}</small>
                        </td>
                        <td className="text-center">
                          {cfg.isActive ? (
                            <Badge bg="success">Active</Badge>
                          ) : (
                            <Badge bg="secondary">Inactive</Badge>
                          )}
                        </td>
                        <td className="text-center">
                          {cfg.isDefault ? (
                            <Badge bg="primary" className="px-2 py-1">
                              <i className="bi bi-star-fill me-1 text-warning"></i>Default
                            </Badge>
                          ) : (
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              className="py-0 px-2"
                              style={{ fontSize: "11px" }}
                              onClick={() => handleSetDefault(cfg.id)}
                            >
                              Set Default
                            </Button>
                          )}
                        </td>
                        <td className="text-end text-nowrap">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-1 py-1 px-2"
                            onClick={() => handleOpenEdit(cfg)}
                            title="Edit Account"
                          >
                            <i className="bi bi-pencil-fill"></i>
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="py-1 px-2"
                            onClick={() => handleDelete(cfg.id, cfg.customerCode)}
                            title="Delete Account"
                            disabled={cfg.isDefault && configs.length > 1}
                          >
                            <i className="bi bi-trash-fill"></i>
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-white">
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add / Edit Modal */}
      <Modal
        show={showEditModal}
        onHide={() => !saving && setShowEditModal(false)}
        size="lg"
        centered
        backdrop="static"
      >
        <Modal.Header closeButton={!saving} className="bg-dark text-white">
          <Modal.Title className="fs-6 fw-bold">
            <i className="bi bi-gear-fill me-2"></i>
            {editingConfig ? `Edit DTDC Account (${editingConfig.customerCode})` : "Add New DTDC Customer Code"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <div className="bg-light border-bottom px-4 pt-3">
            <Nav variant="tabs" activeKey={activeFormTab} onSelect={(k) => setActiveFormTab(k)}>
              <Nav.Item>
                <Nav.Link eventKey="credentials" className="fw-semibold small">
                  <i className="bi bi-key-fill me-1"></i> Credentials & URLs
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="origin" className="fw-semibold small">
                  <i className="bi bi-geo-alt-fill me-1"></i> Origin Details (Pickup)
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="return" className="fw-semibold small">
                  <i className="bi bi-arrow-return-left me-1"></i> Return Details (RTO)
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </div>

          <Modal.Body className="p-4">
            {activeFormTab === "credentials" && (
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small fw-bold">Account Name / Label</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g. Primary Account, Uniform Dispatch"
                      value={formData.accountName}
                      onChange={(e) => setFormData({ ...formData, accountName: e.target.value })}
                    />
                    <Form.Text className="text-muted small">Friendly description for this customer code</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small fw-bold">
                      CUSTOMER_CODE <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      required
                      placeholder="e.g. HL4108"
                      value={formData.customerCode}
                      onChange={(e) => setFormData({ ...formData, customerCode: e.target.value })}
                    />
                    <Form.Text className="text-muted small">DTDC customer identification code</Form.Text>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small fw-bold">
                      API_KEY <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      required
                      placeholder="e.g. 33d59ba7b627b1db..."
                      value={formData.apiKey}
                      onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                    />
                    <Form.Text className="text-muted small">API key for consignment booking and labels</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small fw-bold">
                      TRACKING_TOKEN <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      required
                      placeholder="e.g. HL4108_trk_json:2d2ee967..."
                      value={formData.trackingToken}
                      onChange={(e) => setFormData({ ...formData, trackingToken: e.target.value })}
                    />
                    <Form.Text className="text-muted small">Access token for tracking API queries</Form.Text>
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="small fw-bold">DTDC_BASE_URL</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder={DEFAULT_BASE_URL}
                      value={formData.dtdcBaseUrl}
                      onChange={(e) => setFormData({ ...formData, dtdcBaseUrl: e.target.value })}
                    />
                    <Form.Text className="text-muted small">Consignment softdata / cancellation base endpoint</Form.Text>
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="small fw-bold">DTDC_TRACKING_URL</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder={DEFAULT_TRACKING_URL}
                      value={formData.dtdcTrackingUrl}
                      onChange={(e) => setFormData({ ...formData, dtdcTrackingUrl: e.target.value })}
                    />
                    <Form.Text className="text-muted small">Tracking status API endpoint</Form.Text>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Check
                    type="checkbox"
                    id="chk-default"
                    label="Set as Default DTDC Account"
                    className="fw-semibold mt-2"
                    checked={formData.isDefault}
                    onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  />
                </Col>
                <Col md={6}>
                  <Form.Check
                    type="checkbox"
                    id="chk-active"
                    label="Active"
                    className="fw-semibold mt-2"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                </Col>
              </Row>
            )}

            {activeFormTab === "origin" && (
              <Row className="g-3">
                <Col md={12}>
                  <Alert variant="info" className="py-2 small mb-1">
                    <i className="bi bi-info-circle me-1"></i> These details represent where the DTDC courier picks up parcels for this customer code.
                  </Alert>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small fw-bold">Origin / Sender Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.originDetails.name}
                      onChange={(e) => updateOriginField("name", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small fw-bold">Phone</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.originDetails.phone}
                      onChange={(e) => updateOriginField("phone", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small fw-bold">Alternate Phone</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.originDetails.alternate_phone}
                      onChange={(e) => updateOriginField("alternate_phone", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small fw-bold">Pincode</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.originDetails.pincode}
                      onChange={(e) => updateOriginField("pincode", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="small fw-bold">Address Line 1</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.originDetails.address_line_1}
                      onChange={(e) => updateOriginField("address_line_1", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="small fw-bold">Address Line 2</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.originDetails.address_line_2}
                      onChange={(e) => updateOriginField("address_line_2", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small fw-bold">City</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.originDetails.city}
                      onChange={(e) => updateOriginField("city", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small fw-bold">State</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.originDetails.state}
                      onChange={(e) => updateOriginField("state", e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}

            {activeFormTab === "return" && (
              <Row className="g-3">
                <Col md={12}>
                  <Alert variant="info" className="py-2 small mb-1">
                    <i className="bi bi-info-circle me-1"></i> These details represent where undelivered parcels will be returned (RTO address).
                  </Alert>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small fw-bold">Return Entity Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.returnDetails.name}
                      onChange={(e) => updateReturnField("name", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small fw-bold">Email</Form.Label>
                    <Form.Control
                      type="email"
                      value={formData.returnDetails.email}
                      onChange={(e) => updateReturnField("email", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small fw-bold">Phone</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.returnDetails.phone}
                      onChange={(e) => updateReturnField("phone", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small fw-bold">Alternate Phone</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.returnDetails.alternate_phone}
                      onChange={(e) => updateReturnField("alternate_phone", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="small fw-bold">Address Line 1</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.returnDetails.address_line_1}
                      onChange={(e) => updateReturnField("address_line_1", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="small fw-bold">Address Line 2</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.returnDetails.address_line_2}
                      onChange={(e) => updateReturnField("address_line_2", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="small fw-bold">City Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.returnDetails.city_name}
                      onChange={(e) => updateReturnField("city_name", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="small fw-bold">State Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.returnDetails.state_name}
                      onChange={(e) => updateReturnField("state_name", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="small fw-bold">Pincode</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.returnDetails.pincode}
                      onChange={(e) => updateReturnField("pincode", e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Spinner size="sm" animation="border" className="me-1" />
                  Saving...
                </>
              ) : (
                "Save Configuration"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default DtdcConfigManager;
